'use client';

import { detectBrowserLanguage, getLanguageCookie, setLanguageCookie } from '@skin-seoul/service';
import Cookies from 'js-cookie';
import { useEffect } from 'react';
import { GOOGLE_TRANSLATE_LANGUAGE_CODES } from '../TranslateLanguageSelect/constants';

const GT_ELEMENT_ID = 'google_translate_element';
const PAGE_DEFAULT_LANGAGE = 'en';
const SELECT_SELECTOR = 'select.goog-te-combo';
const DEFAULT_WAIT_TIMEOUT_MS = 2000;
const APEX_DOMAIN_PARTS = 2;
const GOOGLE_TRANSLATE_SCRIPT_URL = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
const TRANSLATE_SCRIPT_SELECTOR = 'script[src^="https://translate.google.com/translate_a/element.js"]';

const HIDE_TRANSLATE_UI_CSS = `
  #google_translate_element { display: none !important; }
  .goog-te-gadget { display: none !important; }
  .goog-te-gadget-simple { display: none !important; }
  .VIpgJd-ZVi9od-xl07Ob-lTBxed { display: none !important; }
  .VIpgJd-ZVi9od-aZ2wEe-wOHMyf .VIpgJd-ZVi9od-aZ2wEe-wOHMyf-ti6hGc { display: none !important; }
  .VIpgJd-ZVi9od-aZ2wEe-wOHMyf.VIpgJd-ZVi9od-aZ2wEe-wOHMyf-ti6hGc { display: none !important; }
  .goog-te-banner-frame { display: none !important; }
  .skiptranslate { display: none !important; }
  body { top: 0 !important; }
`;

const waitForTranslateSelect = (timeoutMs: number = DEFAULT_WAIT_TIMEOUT_MS): Promise<HTMLSelectElement | null> => {
  return new Promise((resolve) => {
    const existing = document.querySelector(SELECT_SELECTOR) as HTMLSelectElement | null;
    if (existing) {
      resolve(existing);
      return;
    }
    const observer = new MutationObserver(() => {
      const sel = document.querySelector(SELECT_SELECTOR) as HTMLSelectElement | null;
      if (sel) {
        observer.disconnect();
        resolve(sel);
      }
    });
    observer.observe(document.documentElement || document.body, { childList: true, subtree: true });
    window.setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(SELECT_SELECTOR) as HTMLSelectElement | null);
    }, timeoutMs);
  });
};

// Window declarations moved to app-level global.d.ts

const getBrowserLanguage = () => {
  const detected = detectBrowserLanguage();
  return GOOGLE_TRANSLATE_LANGUAGE_CODES.includes(detected as (typeof GOOGLE_TRANSLATE_LANGUAGE_CODES)[number])
    ? detected
    : PAGE_DEFAULT_LANGAGE;
};

const getTargetLanguage = () => getLanguageCookie() || getBrowserLanguage();

export function GoogleTranslateScript() {
  useEffect(() => {
    const setGoogTransCookie = (target: string) => {
      const value = `/${PAGE_DEFAULT_LANGAGE}/${target}`;
      const hostParts = window.location.hostname.split('.');
      const apex =
        hostParts.length >= APEX_DOMAIN_PARTS ? `.${hostParts.slice(-APEX_DOMAIN_PARTS).join('.')}` : undefined;
      Cookies.set('googtrans', value, { path: '/' });
      if (apex) {
        Cookies.set('googtrans', value, { domain: apex, path: '/' });
      }
    };

    const earlyTarget = getTargetLanguage();
    setGoogTransCookie(earlyTarget);
    if (!getLanguageCookie()) {
      setLanguageCookie(earlyTarget);
    }

    const applyTargetLanguage = (target: string) => {
      const select = document.querySelector(SELECT_SELECTOR) as HTMLSelectElement | null;
      if (select && select.value !== target) {
        select.value = target;
        select.dispatchEvent(new Event('change'));
      }
    };

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: PAGE_DEFAULT_LANGAGE,
          autoDisplay: false,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        GT_ELEMENT_ID,
      );

      const targetLang = getTargetLanguage();
      waitForTranslateSelect().then(() => applyTargetLanguage(targetLang));
    };

    if (window.google?.translate) {
      window.googleTranslateElementInit?.();
      return;
    }

    const existing = document.querySelector(TRANSLATE_SCRIPT_SELECTOR) as HTMLScriptElement | null;
    if (existing) {
      return;
    }

    const s = document.createElement('script');
    s.src = GOOGLE_TRANSLATE_SCRIPT_URL;
    s.async = true;
    document.body.appendChild(s);

    const handleLanguageChange = (e: Event) => {
      const detail = (e as CustomEvent<string | { language: string }>).detail as
        | string
        | { language: string }
        | undefined;
      const langFromDetailEvent = typeof detail === 'string' ? detail : detail?.language;
      applyTargetLanguage(langFromDetailEvent || getTargetLanguage());
    };

    window.addEventListener('ss:user-language-change', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('ss:user-language-change', handleLanguageChange as EventListener);
    };
  }, []);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: HIDE_TRANSLATE_UI_CSS,
        }}
      />
      <div id={GT_ELEMENT_ID} />
    </>
  );
}

export default GoogleTranslateScript;
